import React from 'react';
import styled from 'styled-components';
import { FiEdit2, FiPhone, FiMail, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import theme from '../../styles/theme';

const CardContainer = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    border-color: ${theme.colors.primary[300]};
  }
`;

const Header = styled.div`
  background: linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[600]} 100%);
  padding: ${theme.spacing[6]};
  display: flex;
  align-items: center;
  color: ${theme.colors.text.primary};
  position: relative;
`;

const Avatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: ${theme.borderRadius.full};
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  text-transform: uppercase;
  border: 3px solid rgba(255, 255, 255, 0.3);
  color: ${theme.colors.text.primary};
`;

const InfoContainer = styled.div`
  flex: 1;
  margin-left: ${theme.spacing[4]};
`;

const Name = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  margin: 0 0 ${theme.spacing[1]} 0;
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: ${theme.fontSizes.sm};
  margin: 0;
`;

const StatusRibbon = styled.div`
  position: absolute;
  top: ${theme.spacing[3]};
  right: ${theme.spacing[3]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.sm};
  font-weight: 800;
  letter-spacing: 0.2px;
  color: #ffffff;
  background: ${props => (props.$active ? '#059669' : '#dc2626')};
  border: 1px solid ${props => (props.$active ? '#047857' : '#b91c1c')};
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
`;

const ContentSection = styled.div`
  padding: ${theme.spacing[4]};
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
`;

const DetailIcon = styled.div`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};
  min-width: 20px;
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  margin-top: ${theme.spacing[4]};
`;

const ActionButton = styled.button`
  flex: 1;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${props => props.$primary ? theme.colors.primary[500] : theme.colors.border.primary};
  background: ${props => props.$primary ? theme.colors.primary[500] : 'transparent'};
  color: ${props => props.$primary ? theme.colors.text.primary : theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${props => props.$primary ? theme.colors.primary[600] : theme.colors.background.primary};
    color: ${props => props.$primary ? theme.colors.text.primary : theme.colors.primary[500]};
    border-color: ${props => props.$primary ? theme.colors.primary[600] : theme.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ActionIcon = styled.div`
  font-size: ${theme.fontSizes.base};
`;

const getInitials = (name) => {
  if (!name) return 'A';
  const words = name.trim().split(' ');
  if (words.length === 1) return words[0][0].toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

const AdministradorCard = ({ administrador }) => {
  const navigate = useNavigate();

  const handleEdit = () => {
    navigate(`/administradores/${administrador.uuid || administrador.id}/editar`);
  };

  return (
    <CardContainer>
      <Header>
        <Avatar>{getInitials(administrador.nome)}</Avatar>
        <InfoContainer>
          <Name>{administrador.nome}</Name>
          <Subtitle>{administrador.email}</Subtitle>
        </InfoContainer>
        <StatusRibbon $active={administrador.ativo}>
          {administrador.ativo ? <FiCheckCircle /> : <FiXCircle />}
          {administrador.ativo ? 'Ativo' : 'Inativo'}
        </StatusRibbon>
      </Header>

      <ContentSection>
        <Details>
          {administrador.telefone && (
            <DetailItem>
              <DetailIcon><FiPhone /></DetailIcon>
              <span>{administrador.telefone}</span>
            </DetailItem>
          )}
          {administrador.email && (
            <DetailItem>
              <DetailIcon><FiMail /></DetailIcon>
              <span>{administrador.email}</span>
            </DetailItem>
          )}
        </Details>

        <Actions>
          <ActionButton $primary onClick={handleEdit}>
            <ActionIcon><FiEdit2 /></ActionIcon>
            Editar
          </ActionButton>
        </Actions>
      </ContentSection>
    </CardContainer>
  );
};

export default AdministradorCard;


